import { THEME } from '@common/constants/theme.constant';
import { Link } from '@common/general-ui/link/link.component';
import { ISerializedStylesList } from '@common/interfaces/serialized-styles-list.interface';
import { ILinkItem } from '@common/utils/make-links/make-links.util';
import { Button as AntButton } from 'antd';
import React, { ReactNode } from 'react';
import { css } from 'twin.macro';

type TButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'tertiary-light' | 'quaternary' | 'lightGrey' | 'accent-small' | 'blue';
interface IProps {
    name: string;
    variant: TButtonVariant;
    disabled?: boolean;
    onClick?: () => void;
    external?: boolean;
    children?: ReactNode;
    type?: 'submit' | 'button';
    selected?: boolean;
    className?: string;
    href?: ILinkItem;
    target?: string;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    shape?: 'round';
}

// eslint-disable-next-line react/display-name
export const ButtonV2 = React.forwardRef<HTMLDivElement, IProps>(
    (
        {
            name,
            type = 'submit',
            icon,
            iconPosition = 'left',
            children,
            external,
            target,
            disabled,
            className,
            selected,
            variant,
            href,
            shape,
            ...rest
        },
        _ref,
    ) => {
        return (
            <AntButton
                name={name}
                {...rest}
                disabled={disabled}
                css={styles.button({ variant, shape })}
                className={className}
                data-testid={`button-${name}`}
                data-selected={selected}
                htmlType={type}
                ref={_ref}
            >
                {href ? (
                    <Link name={`link-${name}`} href={href} noStyle={true} tw="flex" target={target}>
                        {iconPosition === 'left' && icon}
                        {children && <span css={styles.children(!!icon, iconPosition)}> {children}</span>}
                        {iconPosition === 'right' && icon}
                    </Link>
                ) : (
                    <>
                        {iconPosition === 'left' && icon}
                        {children && <span css={styles.children(!!icon, iconPosition)}> {children}</span>}
                        {iconPosition === 'right' && icon}
                    </>
                )}
            </AntButton>
        );
    },
);

// TODO: remove unused styles (variants, size, etc)
const styles = {
    button: ({ variant, shape }: Pick<IProps, 'variant' | 'shape'>): ISerializedStylesList => [
        css`
            display: flex;
            justify-content: center;
            padding: 12px 16px;
            border-radius: 4px;
            align-items: center;
            font-style: normal;
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            height: 48px;

            &&[ant-click-animating-without-extra-node='true']::after {
                display: none;
            }

            span:first-of-type {
                display: flex;
                justify-content: center;
                align-items: center;
            }
            .anticon {
                display: flex;
                justify-content: center;
                align-items: center;
                align-self: center;
            }
        `,

        shape === 'round' &&
        css`
                &&.ant-btn {
                    border-radius: 35px;
                }
            `,

        variant === 'primary' &&
        css`
                &&.ant-btn {
                    background-color: ${THEME.ORANGE};
                    border: 1px solid ${THEME.ORANGE};
                    color: black;

                    :hover {
                        background-color: ${THEME.ORANGE_500};
                        border: 1px solid ${THEME.ORANGE_500};
                        color: black;
                    }

                    :disabled {
                        background-color: ${THEME.GRAY_300};
                        border: 1px solid ${THEME.GRAY_300};
                        color: ${THEME.GRAY};
                    }
                }
            `,

        variant === 'secondary' &&
        css`
                &&.ant-btn {
                    background-color: ${THEME.WHITE};
                    border: 1px solid ${THEME.GRAY_300};
                    color: black;
                    :hover {
                        background-color: ${THEME.WHITE};
                        border: 1px solid ${THEME.BLACK};
                        color: black;
                    }

                    :disabled {
                        border: 1px solid ${THEME.GRAY_300};
                        background-color: ${THEME.GRAY_200};
                        color: #919191;
                    }
                }
            `,

        variant === 'tertiary' &&
        css`
                &&.ant-btn {
                    color: white;
                    background-color: ${THEME.GREEN};
                    border: 1px solid ${THEME.GREEN};
                    :hover {
                        background-color: #1f3d24;
                        color: white;
                    }

                    :disabled {
                        background-color: ${THEME.GRAY_300};
                        border: 1px solid ${THEME.GRAY_300};
                        color: #767676;
                    }
                }
            `,
        variant === 'tertiary-light' &&
        css`
        &&.ant-btn {
            color: white;
            background-color: ${THEME.LIGHT_GREEN};
            border: 1px solid ${THEME.LIGHT_GREEN};
            :hover {
                background-color: ${THEME.GREEN};
                color: white;
            }

            :disabled {
                background-color: ${THEME.GRAY_300};
                border: 1px solid ${THEME.GRAY_300};
                color: #767676;
            }
        }
    `,

        variant === 'quaternary' &&
        css`
                &&.ant-btn {
                    color: white;
                    background-color: ${THEME.PINK};
                    border: 1px solid ${THEME.PINK};
                    :hover {
                        background-color: ${THEME.PINK_HOVER};
                        border: 1px solid ${THEME.PINK_HOVER};
                        color: white;
                    }

                    :disabled {
                        background-color: ${THEME.GRAY_300};
                        border: 1px solid ${THEME.GRAY_300};
                        color: #767676;
                    }
                }
            `,
        variant === 'blue' &&
        css`
                &&.ant-btn {
                    color: white;
                    background-color: ${THEME.BLUE};
                    border: 1px solid ${THEME.BLUE};
                    :hover {
                        background-color: ${THEME.DARK_BLUE};
                        color: white;
                    }

                    :disabled {
                        background-color: ${THEME.GRAY_300};
                        border: 1px solid ${THEME.GRAY_300};
                        color: #767676;
                    }
                }
            `,
        variant === 'accent-small' &&
        css`
                &&.ant-btn {
                    font-size: 14px;
                    font-weight: 300;
                    justify-content: flex-start;
                    background-color: ${THEME.WHITE};
                    border: 1px solid ${THEME.GRAY_100};
                    box-shadow: none;
                    color: black;

                    &&[data-selected='true'] {
                        border: 1px solid ${THEME.PINK};
                    }

                    && .anticon {
                        color: ${THEME.PINK};
                    }

                    :hover {
                        background-color: ${THEME.GRAY_200};
                        color: black;
                    }

                    :disabled {
                        border: 1px solid ${THEME.GRAY_300};
                        background-color: ${THEME.GRAY_200};
                        color: #919191;
                    }
                }
            `,

        variant === 'lightGrey' &&
        css`
                &&.ant-btn {
                    padding: 6px 8px;
                    border-radius: 6px;
                    font-weight: 300;
                    font-size: 16px;
                    line-height: 24px;
                    height: 36px;
                    border: none;
                    color: ${THEME.TEXT_BLACK};
                    background-color: ${THEME.GRAY_200};
                    :hover {
                        background-color: ${THEME.GRAY_100};
                        border: none;
                        color: ${THEME.TEXT_BLACK};
                    }
                    :disabled {
                        background-color: ${THEME.GRAY_200};
                        color: #919191;
                        border: none;
                    }
                }
                .anticon {
                    color: ${THEME.TEXT_BLACK};
                    width: 12px;
                    height: 12px;
                }
            `,
    ],
    children: (hasIcon: boolean, iconPosition: string): ISerializedStylesList => [
        css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
        hasIcon &&
        css`
                padding-left: 8px;
                ${iconPosition === 'right' &&
            css`
                    padding-left: 0;
                    padding-right: 6px;
                `}
            `,
    ],
};
